import { useState, useMemo, useEffect } from 'react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import { api } from '../services/api';
import { BomCalcOptions } from '../utils/bomCalculator';
import clsx from 'clsx';

type ReportType = 'production' | 'sales' | 'inventory' | 'dealer' | 'margin' | 'forecast' | 'lost-quotes' | 'extrusion-po' | 'cogs' | 'labor';
type DateRange = '7d' | '30d' | 'mtd' | 'ytd' | 'custom';

// Margin matrix dimensions — matches pricing matrix buckets
const MARGIN_WIDTHS = [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288];
const MARGIN_HEIGHTS = [84, 96, 108, 120, 132, 144, 156, 168, 180, 192];

// MSRP Pricing Matrices - matches BomPage pricing
type ProductType = 'cvw' | 'solar94' | 'solar99' | 'insect';

const PRICING_MATRICES: Record<ProductType, {
  heights: number[];
  widths: number[];
  prices: Record<number, number[]>;
}> = {
  solar94: {
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3275, 3376, 3480, 3588, 3699, 3813, 3931, 4053, 4178, 4308, 4441, 4578, 4720, 4866, 5016, 5171, 5331, 5496, 5666, 5841, 6022],
      96: [3378, 3483, 3590, 3701, 3816, 3934, 4055, 4181, 4310, 4444, 4581, 4723, 4869, 5019, 5174, 5335, 5500, 5670, 5845, 6026, 6212],
      108: [3445.40, 3552, 3662, 3775, 3892, 4012, 4136, 4264, 4396, 4532, 4672, 4817, 4966, 5119, 5278, 5441, 5609, 5783, 5961, 6146, 6336],
      120: [3548.60, 3658, 3771, 3888, 4008, 4132, 4260, 4392, 4528, 4668, 4812, 4961, 5114, 5273, 5436, 5604, 5777, 5956, 6140, 6330, 6526],
      132: [3615.70, 3728, 3843, 3962, 4084, 4211, 4341, 4475, 4613, 4756, 4903, 5055, 5211, 5372, 5538, 5710, 5886, 6068, 6256, 6450, 6649],
      144: [3770.80, 3887, 4008, 4132, 4259, 4391, 4527, 4667, 4811, 4960, 5113, 5272, 5435, 5603, 5776, 5955, 6139, 6329, 6524, 6726, 6934],
      156: [4138.20, 4266, 4398, 4534, 4674, 4819, 4968, 5122, 5280, 5443, 5612, 5785, 5964, 6149, 6339, 6535, 6737, 6945, 7160, 7382, 7610],
      168: [4244.90, 4376, 4512, 4651, 4795, 4943, 5096, 5254, 5416, 5584, 5756, 5934, 6118, 6307, 6502, 6703, 6911, 7124, 7345, 7572, 7806],
      180: [4315.30, 4449, 4586, 4728, 4874, 5025, 5181, 5341, 5506, 5676, 5852, 6033, 6219, 6412, 6610, 6815, 7025, 7243, 7467, 7697, 7936],
      192: [4423.10, 4560, 4701, 4846, 4996, 5151, 5310, 5474, 5644, 5818, 5998, 6184, 6375, 6572, 6775, 6985, 7201, 7423, 7653, 7890, 8134]
    }
  },
  solar99: {
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3438.75, 3544.80, 3654.00, 3767.40, 3883.95, 4003.65, 4127.55, 4255.65, 4386.90, 4523.40, 4663.05, 4806.90, 4956.00, 5109.30, 5266.80, 5429.55, 5597.55, 5770.80, 5949.30, 6133.05, 6323.10],
      96: [3546.90, 3657.15, 3769.50, 3886.05, 4006.80, 4130.70, 4257.75, 4390.05, 4525.50, 4666.20, 4810.05, 4959.15, 5112.45, 5269.95, 5432.70, 5601.75, 5775.00, 5953.50, 6137.25, 6327.30, 6522.60],
      108: [3617.67, 3729.60, 3845.10, 3963.75, 4086.60, 4212.60, 4342.80, 4477.20, 4615.80, 4758.60, 4905.60, 5057.85, 5214.30, 5374.95, 5541.90, 5713.05, 5889.45, 6072.15, 6259.05, 6453.30, 6652.80],
      120: [3725.73, 3840.90, 3959.55, 4082.40, 4208.40, 4338.60, 4473.00, 4611.60, 4754.40, 4901.40, 5052.60, 5209.05, 5369.70, 5536.65, 5707.80, 5884.20, 6065.85, 6253.80, 6447.00, 6646.50, 6852.30],
      132: [3796.49, 3914.40, 4035.15, 4160.10, 4288.20, 4421.55, 4558.05, 4698.75, 4843.65, 4993.80, 5148.15, 5307.75, 5471.55, 5640.60, 5814.90, 5995.50, 6180.30, 6371.40, 6568.80, 6772.50, 6981.45],
      144: [3959.34, 4081.35, 4208.40, 4338.60, 4471.95, 4610.55, 4753.35, 4900.35, 5051.55, 5208.00, 5368.65, 5535.60, 5706.75, 5883.15, 6064.80, 6252.75, 6445.95, 6645.45, 6850.20, 7062.30, 7280.70],
      156: [4345.11, 4479.30, 4617.90, 4760.70, 4907.70, 5059.95, 5216.40, 5378.10, 5544.00, 5715.15, 5892.60, 6074.25, 6262.20, 6456.45, 6655.95, 6861.75, 7073.85, 7292.25, 7518.00, 7751.10, 7990.50],
      168: [4457.15, 4594.80, 4737.60, 4883.55, 5034.75, 5190.15, 5350.80, 5516.70, 5686.80, 5863.20, 6043.80, 6230.70, 6423.90, 6622.35, 6827.10, 7038.15, 7256.55, 7480.20, 7712.25, 7950.60, 8196.30],
      180: [4531.07, 4671.45, 4815.30, 4964.40, 5117.70, 5276.25, 5440.05, 5608.05, 5781.30, 5959.80, 6144.60, 6334.65, 6529.95, 6732.60, 6940.50, 7155.75, 7376.25, 7605.15, 7840.35, 8081.85, 8332.80],
      192: [4644.26, 4788.00, 4936.05, 5088.30, 5245.80, 5408.55, 5575.50, 5747.70, 5926.20, 6108.90, 6297.90, 6493.20, 6693.75, 6900.60, 7113.75, 7334.25, 7561.05, 7794.15, 8035.65, 8284.50, 8540.70]
    }
  },
  cvw: {
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3988.60, 4111.96, 4239.13, 4370.24, 4505.40, 4644.74, 4788.40, 4936.49, 5089.17, 5246.56, 5408.83, 5576.11, 5748.57, 5926.36, 6109.65, 6298.61, 6493.41, 6694.24, 6901.27, 7114.72, 7334.76],
      96: [4107.40, 4234.43, 4365.39, 4500.41, 4639.59, 4783.09, 4931.05, 5083.62, 5240.94, 5403.15, 5570.39, 5742.81, 5920.56, 6103.77, 6291.62, 6486.21, 6686.81, 6893.62, 7106.83, 7326.63, 7553.22],
      108: [4230.60, 4361.44, 4496.33, 4635.40, 4778.76, 4926.55, 5078.92, 5235.99, 5397.99, 5565.06, 5737.35, 5914.99, 6098.14, 6286.93, 6480.34, 6680.76, 6887.38, 7100.39, 7319.99, 7546.39, 7779.78],
      120: [4358.20, 4492.99, 4631.95, 4775.20, 4922.89, 5075.15, 5232.11, 5393.93, 5560.75, 5732.73, 5910.03, 6092.82, 6281.25, 6475.52, 6675.79, 6882.26, 7095.11, 7314.55, 7540.77, 7773.99, 8014.43],
      132: [4489.10, 4627.94, 4771.07, 4918.63, 5070.75, 5227.58, 5389.26, 5555.93, 5727.77, 5904.92, 6087.54, 6275.82, 6469.91, 6670.01, 6876.30, 7088.97, 7308.22, 7534.25, 7767.26, 8007.49, 8255.14],
      144: [4623.30, 4766.29, 4913.70, 5065.67, 5222.34, 5383.86, 5550.37, 5722.03, 5898.99, 6081.44, 6269.53, 6463.43, 6663.33, 6869.41, 7081.87, 7300.89, 7526.70, 7759.48, 7999.46, 8246.87, 8501.93],
      156: [4761.90, 4909.18, 5061.01, 5217.53, 5378.90, 5545.26, 5716.76, 5893.57, 6075.84, 6263.75, 6457.48, 6657.19, 6863.09, 7075.35, 7294.17, 7519.76, 7752.33, 7992.10, 8239.28, 8494.10, 8756.80],
      168: [4904.90, 5056.60, 5212.99, 5374.21, 5540.43, 5711.78, 5888.43, 6070.55, 6258.30, 6451.85, 6651.40, 6857.11, 7069.18, 7287.82, 7513.22, 7745.58, 7985.14, 8232.10, 8486.70, 8749.18, 9019.77],
      180: [5052.30, 5208.56, 5369.65, 5535.72, 5706.93, 5883.43, 6065.39, 6252.98, 6446.37, 6645.74, 6851.28, 7063.18, 7281.63, 7506.83, 7739.00, 7978.35, 8225.10, 8479.49, 8741.74, 9012.10, 9290.83],
      192: [5204.10, 5365.05, 5530.98, 5702.04, 5878.39, 6060.20, 6247.63, 6440.85, 6640.06, 6845.42, 7057.13, 7275.39, 7500.41, 7732.38, 7971.52, 8218.07, 8472.23, 8734.26, 9004.39, 9282.88, 9569.98]
    }
  },
  insect: {
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3158, 3256, 3356, 3460, 3567, 3678, 3791, 3909, 4030, 4154, 4283, 4415, 4552, 4692, 4838, 4987, 5141, 5300, 5464, 5633, 5808],
      96: [3256, 3357, 3461, 3568, 3678, 3792, 3909, 4030, 4154, 4283, 4415, 4552, 4693, 4838, 4987, 5142, 5301, 5465, 5634, 5808, 5988],
      108: [3318, 3420, 3526, 3635, 3747, 3863, 3983, 4106, 4233, 4364, 4499, 4638, 4781, 4929, 5082, 5239, 5401, 5568, 5740, 5918, 6101],
      120: [3414, 3520, 3629, 3741, 3857, 3976, 4099, 4226, 4357, 4491, 4630, 4773, 4921, 5073, 5230, 5392, 5559, 5731, 5908, 6090, 6279],
      132: [3475, 3582, 3693, 3807, 3925, 4047, 4172, 4301, 4434, 4571, 4712, 4858, 5008, 5163, 5323, 5487, 5657, 5832, 6012, 6198, 6390],
      144: [3625, 3737, 3852, 3971, 4094, 4221, 4351, 4486, 4625, 4768, 4915, 5067, 5224, 5385, 5552, 5724, 5901, 6083, 6271, 6465, 6665],
      156: [3999, 4122, 4250, 4381, 4517, 4656, 4800, 4949, 5102, 5260, 5422, 5590, 5763, 5941, 6125, 6314, 6510, 6711, 6919, 7133, 7353],
      168: [4090, 4216, 4347, 4481, 4620, 4763, 4910, 5062, 5218, 5380, 5546, 5718, 5894, 6077, 6265, 6458, 6658, 6864, 7076, 7295, 7521],
      180: [4155, 4283, 4416, 4552, 4693, 4838, 4988, 5142, 5301, 5465, 5634, 5808, 5988, 6173, 6364, 6561, 6764, 6973, 7189, 7411, 7640],
      192: [4256, 4388, 4523, 4663, 4807, 4956, 5109, 5267, 5430, 5598, 5771, 5950, 6134, 6324, 6519, 6721, 6929, 7143, 7364, 7592, 7826]
    }
  }
};

// Get MSRP from pricing matrix (same logic as BomPage)
function getMSRPPrice(width: number, height: number, productType: ProductType = 'solar94'): number {
  const matrix = PRICING_MATRICES[productType];
  if (!matrix) return 0;

  const minWidth = matrix.widths[0];   // 48
  const maxWidth = matrix.widths[matrix.widths.length - 1];
  const minHeight = matrix.heights[0]; // 84
  const maxHeight = matrix.heights[matrix.heights.length - 1];

  if (width > maxWidth || height > maxHeight) return 0;

  // Round up to next bucket, with minimum of 48" width and 84" height
  const heightBucket = height <= minHeight
    ? minHeight
    : matrix.heights.find(h => h >= height) || maxHeight;

  const widthBucket = width <= minWidth
    ? minWidth
    : matrix.widths.find(w => w >= width) || maxWidth;

  const widthIndex = matrix.widths.indexOf(widthBucket);
  const prices = matrix.prices[heightBucket];

  if (!prices || widthIndex < 0 || widthIndex >= prices.length) return 0;

  return prices[widthIndex];
}

export default function ReportsPage() {
  const [reportType, setReportType] = useState<ReportType>('production');
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<any>(null);

  // For margin report — preload inventory + Full Screen BOM template
  const [marginData, setMarginData] = useState<any[] | null>(null);
  const [dealerDiscount, setDealerDiscount] = useState<number>(40);
  const [inventoryItems, setInventoryItems] = useState<any[]>([]);
  const [fullScreenItemIds, setFullScreenItemIds] = useState<string[]>([]);
  const [bomDataLoaded, setBomDataLoaded] = useState(false);
  const [fabricPartMap, setFabricPartMap] = useState<Record<string, Record<string, string>>>({});
  // For forecast report
  const [forecastDays, setForecastDays] = useState<number>(90);
  // For extrusion PO report
  const [extrusionLeadTime, setExtrusionLeadTime] = useState(45);
  const [extrusionCoverage, setExtrusionCoverage] = useState(90);
  const [extrusionSafetyPct, setExtrusionSafetyPct] = useState(15);

  // Load inventory + Full Screen BOM template on mount (for margin report)
  useEffect(() => {
    const loadBomData = async () => {
      try {
        const [items, bomTemplates, catalogData] = await Promise.all([
          api.inventory.getAll({ limit: 10000 }),
          api.get('/bom/custom/list'),
          api.fabricSettings.getCatalog().catch(() => ({ fabricTypes: [] })),
        ]);
        setInventoryItems(items || []);
        // Build fabric part map from catalog
        if (catalogData?.fabricTypes?.length > 0) {
          const map: Record<string, Record<string, string>> = {};
          for (const ft of catalogData.fabricTypes) {
            map[ft.code] = {};
            for (const c of ft.colors) {
              if (c.partNumber) map[ft.code][c.code] = c.partNumber;
            }
          }
          setFabricPartMap(map);
        }
        const fullScreenBom = (bomTemplates || []).find((b: any) => b.name === '__FULL_SCREEN_UNIT_DEFAULT__');
        if (fullScreenBom) {
          const details = await api.get(`/bom/custom/${fullScreenBom.id}`);
          if (details?.items) {
            setFullScreenItemIds(details.items.map((i: any) => i.inventoryItemId || i.itemId || i.id));
          }
        }
        setBomDataLoaded(true);
      } catch (err) {
        console.error('Failed to preload BOM data:', err);
      }
    };
    loadBomData();
  }, []);

  const getDateRange = () => {
    const now = new Date();
    switch (dateRange) {
      case '7d':
        return { start: subDays(now, 7), end: now };
      case '30d':
        return { start: subDays(now, 30), end: now };
      case 'mtd':
        return { start: startOfMonth(now), end: now };
      case 'ytd':
        return { start: new Date(now.getFullYear(), 0, 1), end: now };
      case 'custom':
        return {
          start: customStart ? new Date(customStart) : subDays(now, 30),
          end: customEnd ? new Date(customEnd) : now
        };
      default:
        return { start: subDays(now, 30), end: now };
    }
  };

  // Recalculate margin data when discount changes (if we already have BOM costs)
  const adjustedMarginData = useMemo(() => {
    if (!marginData) return null;
    return marginData.map(row => {
      const dealerPrice = row.msrp * (1 - dealerDiscount / 100);
      const grossMargin = dealerPrice - row.bomCost;
      const marginPercent = dealerPrice > 0 ? (grossMargin / dealerPrice) * 100 : 0;
      return { ...row, dealerPrice, grossMargin, marginPercent };
    });
  }, [marginData, dealerDiscount]);

  const generateReport = async () => {
    if (reportType === 'margin') {
      if (!bomDataLoaded || inventoryItems.length === 0 || fullScreenItemIds.length === 0) {
        alert('BOM data still loading. Please wait a moment and try again.');
        return;
      }
      setLoading(true);
      try {
        // Use exact same BOM calculation as BomPage — same items, same logic, same result
        const bomOptions: BomCalcOptions = {
          systemType: 'v_track',
          uChannel: 'none',
          screwCover: 'none',
          brushSize: '1',
          shippingMethod: 'will_call',
          windowPanel: false,
          windowCount: 0,
          frameColor: 'black',
          fabricType: 'omega_pro_3',
          fabricColor: 'black',
          motorType: 'alpha',
        };

        // Costed by the server (BomService) — the same engine that produces a
        // released order's BOM and cut sheet. This used to run a client-side
        // reimplementation (utils/bomCalculator.ts) over all 210 sizes, which
        // was a third copy of the proration and filter rules and drifted from
        // what the floor actually builds.
        const grid = await api.bom.calculateBomGrid({
          widths: MARGIN_WIDTHS,
          heights: MARGIN_HEIGHTS,
          motorType: bomOptions.motorType,
          brushSize: bomOptions.brushSize,
          fabricType: bomOptions.fabricType,
          fabricColor: bomOptions.fabricColor,
          systemColor: bomOptions.frameColor,
          systemType: bomOptions.systemType,
          uChannelLeft: false,
          uChannelRight: false,
          applyUChannelDeductions: false,
          applyBoxDeduction: true,
        });

        const results: any[] = (grid.results || []).map(r => ({
          width: r.width,
          height: r.height,
          sqFt: ((r.width * r.height) / 144).toFixed(1),
          bomCost: r.totalBomCost,
          bomItemCount: r.itemCount,
          msrp: getMSRPPrice(r.width, r.height),
        }));
        setMarginData(results);
        setReportData({ generated: true });
      } catch (err: any) {
        console.error('Margin calculation failed:', err);
        alert('BOM calculation failed: ' + (err?.message || 'Unknown error'));
      } finally {
        setLoading(false);
      }
      return;
    }

    const { start, end } = getDateRange();
    setLoading(true);

    try {
      let data;
      switch (reportType) {
        case 'production':
          data = await api.reports.getProductionReport({
            startDate: format(start, 'yyyy-MM-dd'),
            endDate: format(end, 'yyyy-MM-dd'),
          });
          break;
        case 'sales':
          data = await api.reports.getSalesReport({
            startDate: format(start, 'yyyy-MM-dd'),
            endDate: format(end, 'yyyy-MM-dd'),
          });
          break;
        case 'inventory':
          data = await api.reports.getInventoryReport();
          break;
        case 'dealer':
          data = await api.reports.getDealerReport({
            startDate: format(start, 'yyyy-MM-dd'),
            endDate: format(end, 'yyyy-MM-dd'),
          });
          break;
        case 'forecast':
          data = await api.reports.getRevenueForecastReport(forecastDays);
          break;
        case 'lost-quotes':
          data = await api.reports.getLostQuotesReport(
            format(start, 'yyyy-MM-dd'),
            format(end, 'yyyy-MM-dd'),
          );
          break;
        case 'cogs':
          data = await api.reports.getCogsReport(
            format(start, 'yyyy-MM-dd'),
            format(end, 'yyyy-MM-dd'),
          );
          break;
        case 'labor':
          data = await api.get(
            `/production/labor-bom/daily?from=${format(start, 'yyyy-MM-dd')}&to=${format(end, 'yyyy-MM-dd')}`,
          );
          break;
        case 'extrusion-po': {
          const periodDays = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
          data = await api.reports.getExtrusionPOReport({
            lookbackDays: periodDays,
            leadTimeDays: extrusionLeadTime,
            targetCoverageDays: extrusionCoverage,
            safetyStockPct: extrusionSafetyPct,
          });
          break;
        }
        case 'showroom': {
          const token = localStorage.getItem('admin_auth_token');
          const res = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/accounting/showroom-impact`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          data = await res.json();
          break;
        }
      }
      setReportData(data);
    } catch (error) {
      console.error('Failed to generate report:', error);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Reports"
        subtitle="Generate and export business reports"
      />

      {/* Report Configuration */}
      <Card className="mb-6 no-print">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
            <select
              value={reportType}
              onChange={(e) => {
                setReportType(e.target.value as ReportType);
                setReportData(null);
              }}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="production">Production Report</option>
              <option value="sales">Sales Report</option>
              <option value="inventory">Inventory Report</option>
              <option value="dealer">Dealer Report</option>
              <option value="margin">Margin Analysis</option>
              <option value="forecast">Revenue Forecast</option>
              <option value="lost-quotes">Lost Quotes</option>
              <option value="cogs">Monthly COGS</option>
              <option value="labor">Daily Labor BOM</option>
              <option value="extrusion-po">Extrusion Purchase Order</option>
              <option value="showroom">Showroom Impact Analysis</option>
            </select>
          </div>

          {reportType === 'forecast' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lookback Period</label>
              <select
                value={forecastDays}
                onChange={(e) => setForecastDays(parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="30">Last 30 Days</option>
                <option value="60">Last 60 Days</option>
                <option value="90">Last 90 Days</option>
                <option value="180">Last 6 Months</option>
                <option value="365">Last Year</option>
              </select>
            </div>
          )}

          {reportType === 'extrusion-po' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lead Time (days)</label>
                <input
                  type="number"
                  value={extrusionLeadTime}
                  onChange={(e) => setExtrusionLeadTime(parseInt(e.target.value) || 45)}
                  onWheel={(e) => e.currentTarget.blur()}
                  min="1"
                  max="120"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Coverage (days)</label>
                <input
                  type="number"
                  value={extrusionCoverage}
                  onChange={(e) => setExtrusionCoverage(parseInt(e.target.value) || 90)}
                  onWheel={(e) => e.currentTarget.blur()}
                  min="1"
                  max="365"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Safety Stock %</label>
                <input
                  type="number"
                  value={extrusionSafetyPct}
                  onChange={(e) => setExtrusionSafetyPct(parseInt(e.target.value) || 0)}
                  onWheel={(e) => e.currentTarget.blur()}
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}

          {!['margin', 'forecast'].includes(reportType) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as DateRange)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="mtd">Month to Date</option>
                <option value="ytd">Year to Date</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
          )}

          {reportType === 'margin' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dealer Discount %</label>
              <input
                type="number"
                value={dealerDiscount}
                onChange={(e) => setDealerDiscount(parseFloat(e.target.value) || 0)}
                onWheel={(e) => e.currentTarget.blur()}
                min="0"
                max="100"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {dateRange === 'custom' && !['margin', 'forecast'].includes(reportType) && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}

          <div className="flex items-end gap-2">
            <button
              onClick={generateReport}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Calculating...' : 'Generate Report'}
            </button>
            {reportData && (
              <button
                onClick={() => window.print()}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                title="Print report"
              >
                Print
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* Report Content */}
      {loading ? (
        <Card>
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        </Card>
      ) : reportType === 'margin' && adjustedMarginData ? (
        <MarginReport data={adjustedMarginData} dealerDiscount={dealerDiscount} />
      ) : reportType === 'forecast' && reportData ? (
        <ForecastReport data={reportData} />
      ) : reportType === 'lost-quotes' && reportData ? (
        <LostQuotesReport data={reportData} />
      ) : reportType === 'cogs' && reportData ? (
        <CogsReport data={reportData} />
      ) : reportType === 'labor' && reportData ? (
        <DailyLaborBomReport data={reportData} />
      ) : reportType === 'extrusion-po' && reportData ? (
        <ExtrusionPOReportView data={reportData} params={{ lookbackDays: (() => { const { start, end } = getDateRange(); return Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))); })(), leadTimeDays: extrusionLeadTime, targetCoverageDays: extrusionCoverage, safetyStockPct: extrusionSafetyPct }} />
      ) : reportType === 'showroom' && reportData ? (
        <ShowroomImpactReport data={reportData} />
      ) : reportData ? (
        <ReportContent type={reportType} data={reportData} />
      ) : (
        <Card>
          <div className="text-center py-12 text-gray-500">
            {reportType === 'margin'
              ? 'Click "Generate Report" to calculate margin analysis based on current inventory costs'
              : reportType === 'extrusion-po'
              ? 'Click "Generate Report" to analyze extrusion inventory and generate purchase order recommendations'
              : 'Select report options and click "Generate Report" to view data'
            }
          </div>
        </Card>
      )}
    </div>
  );
}

function CogsReport({ data }: { data: { months: any[]; totals: any; orders: any[] } }) {
  const [showOrders, setShowOrders] = useState(false);
  const fmt = (n: number) => '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const pct = (n: number) => n.toFixed(1) + '%';

  const { months, totals, orders } = data;

  if (!months || months.length === 0) {
    return <Card><div className="text-center py-12 text-gray-500">No shipped orders found in this date range.</div></Card>;
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <div className="text-center">
            <p className="text-sm text-gray-500">Revenue</p>
            <p className="text-xl font-bold text-gray-900">{fmt(totals.revenue)}</p>
            <p className="text-xs text-gray-400">{totals.orderCount} orders / {totals.unitCount} units</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-sm text-gray-500">Material Cost</p>
            <p className="text-xl font-bold text-red-600">{fmt(totals.materialCost)}</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-sm text-gray-500">Labor Cost</p>
            <p className="text-xl font-bold text-orange-600">{fmt(totals.laborCost)}</p>
            {totals.laborCost === 0 && <p className="text-xs text-orange-400">No labor data</p>}
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-sm text-gray-500">Freight Cost</p>
            <p className="text-xl font-bold text-blue-600">{fmt(totals.freightCost)}</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-sm text-gray-500">Gross Margin</p>
            <p className={`text-xl font-bold ${totals.marginPercent >= 50 ? 'text-green-600' : totals.marginPercent >= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
              {pct(totals.marginPercent)}
            </p>
            <p className="text-xs text-gray-400">{fmt(totals.grossProfit)} profit</p>
          </div>
        </Card>
      </div>

      {totals.partsOrders > 0 && (
        <Card>
          <p className="text-sm text-gray-500">Parts Orders: {totals.partsOrders} orders, {fmt(totals.partsRevenue)} revenue (not included in COGS — no BOM)</p>
        </Card>
      )}

      {/* Monthly Breakdown */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Breakdown</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="px-4 py-2 text-left font-medium text-gray-600">Month</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Orders</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Units</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Revenue</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Collected</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Material</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Labor</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Freight</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Total COGS</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Gross Profit</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Margin</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m: any) => (
                <tr key={m.month} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium text-gray-900">{m.month}</td>
                  <td className="px-4 py-2 text-right">{m.orderCount}</td>
                  <td className="px-4 py-2 text-right">{m.unitCount}</td>
                  <td className="px-4 py-2 text-right font-medium">{fmt(m.revenue)}</td>
                  <td className="px-4 py-2 text-right text-gray-500">{fmt(m.collected)}</td>
                  <td className="px-4 py-2 text-right text-red-600">{fmt(m.materialCost)}</td>
                  <td className="px-4 py-2 text-right text-orange-600">{fmt(m.laborCost)}</td>
                  <td className="px-4 py-2 text-right text-blue-600">{fmt(m.freightCost)}</td>
                  <td className="px-4 py-2 text-right font-medium text-red-700">{fmt(m.totalCogs)}</td>
                  <td className="px-4 py-2 text-right font-medium text-green-700">{fmt(m.grossProfit)}</td>
                  <td className={`px-4 py-2 text-right font-bold ${m.marginPercent >= 50 ? 'text-green-600' : m.marginPercent >= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {pct(m.marginPercent)}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                <td className="px-4 py-2">Total</td>
                <td className="px-4 py-2 text-right">{totals.orderCount}</td>
                <td className="px-4 py-2 text-right">{totals.unitCount}</td>
                <td className="px-4 py-2 text-right">{fmt(totals.revenue)}</td>
                <td className="px-4 py-2 text-right text-gray-500">{fmt(totals.collected)}</td>
                <td className="px-4 py-2 text-right text-red-600">{fmt(totals.materialCost)}</td>
                <td className="px-4 py-2 text-right text-orange-600">{fmt(totals.laborCost)}</td>
                <td className="px-4 py-2 text-right text-blue-600">{fmt(totals.freightCost)}</td>
                <td className="px-4 py-2 text-right text-red-700">{fmt(totals.totalCogs)}</td>
                <td className="px-4 py-2 text-right text-green-700">{fmt(totals.grossProfit)}</td>
                <td className={`px-4 py-2 text-right ${totals.marginPercent >= 50 ? 'text-green-600' : totals.marginPercent >= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {pct(totals.marginPercent)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Order Detail Toggle */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Order Detail</h3>
          <button
            onClick={() => setShowOrders(!showOrders)}
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {showOrders ? 'Hide' : 'Show'} {orders.length} Orders
          </button>
        </div>
        {showOrders && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Order</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Dealer</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Units</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Revenue</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Material</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Labor</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Freight</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">COGS</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Margin</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Shipped</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o: any) => (
                  <tr key={o.id} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <a href={`/orders/${o.id}`} className="text-blue-600 hover:underline font-medium">{o.orderNumber}</a>
                      {o.sidemark && <div className="text-xs text-gray-400">{o.sidemark}</div>}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{o.dealer}</td>
                    <td className="px-3 py-2 text-right">{o.unitCount}</td>
                    <td className="px-3 py-2 text-right font-medium">{fmt(o.revenue)}</td>
                    <td className="px-3 py-2 text-right text-red-600">{fmt(o.materialCost)}</td>
                    <td className="px-3 py-2 text-right text-orange-600">{fmt(o.laborCost)}</td>
                    <td className="px-3 py-2 text-right text-blue-600">{fmt(o.freightCost)}</td>
                    <td className="px-3 py-2 text-right font-medium text-red-700">{fmt(o.totalCogs)}</td>
                    <td className={`px-3 py-2 text-right font-bold ${o.marginPercent >= 50 ? 'text-green-600' : o.marginPercent >= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {pct(o.marginPercent)}
                    </td>
                    <td className="px-3 py-2 text-gray-500 text-xs">{new Date(o.shippedAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function MarginReport({ data, dealerDiscount }: { data: any[]; dealerDiscount: number }) {
  const [selectedHeight, setSelectedHeight] = useState<number | 'all'>('all');

  // Build lookup and dimensions
  const heights = [...new Set(data.map((d: any) => d.height))].sort((a, b) => a - b);
  const widths = [...new Set(data.map((d: any) => d.width))].sort((a, b) => a - b);
  const lookup = new Map<string, any>();
  data.forEach((d: any) => lookup.set(`${d.height}-${d.width}`, d));

  // Summary stats
  const avgMargin = data.reduce((sum: number, d: any) => sum + d.marginPercent, 0) / data.length;
  const minEntry = data.reduce((min: any, d: any) => d.marginPercent < min.marginPercent ? d : min, data[0]);
  const maxEntry = data.reduce((max: any, d: any) => d.marginPercent > max.marginPercent ? d : max, data[0]);
  const avgBomCost = data.reduce((sum: number, d: any) => sum + d.bomCost, 0) / data.length;

  // Detail data filtered by height
  const detailData = selectedHeight === 'all' ? data : data.filter((d: any) => d.height === selectedHeight);

  // Avg margin per height for chart
  const marginByHeight = heights.map(h => {
    const rows = data.filter((d: any) => d.height === h);
    const avg = rows.reduce((s: number, r: any) => s + r.marginPercent, 0) / rows.length;
    return { height: h, avgMargin: avg };
  });

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="text-center">
          <p className={clsx(
            'text-2xl font-bold',
            avgMargin >= 50 ? 'text-green-600' : avgMargin >= 30 ? 'text-yellow-600' : 'text-red-600'
          )}>
            {avgMargin.toFixed(1)}%
          </p>
          <p className="text-sm text-gray-500">Avg Margin</p>
        </Card>
        <Card className="text-center">
          <p className={clsx(
            'text-2xl font-bold',
            minEntry.marginPercent >= 50 ? 'text-green-600' : minEntry.marginPercent >= 30 ? 'text-yellow-600' : 'text-red-600'
          )}>
            {minEntry.marginPercent.toFixed(1)}%
          </p>
          <p className="text-sm text-gray-500">Min ({minEntry.width/12}' x {minEntry.height/12}')</p>
        </Card>
        <Card className="text-center">
          <p className={clsx(
            'text-2xl font-bold',
            maxEntry.marginPercent >= 50 ? 'text-green-600' : maxEntry.marginPercent >= 30 ? 'text-yellow-600' : 'text-red-600'
          )}>
            {maxEntry.marginPercent.toFixed(1)}%
          </p>
          <p className="text-sm text-gray-500">Max ({maxEntry.width/12}' x {maxEntry.height/12}')</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">${avgBomCost.toFixed(0)}</p>
          <p className="text-sm text-gray-500">Avg BOM Cost</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">{data.length}</p>
          <p className="text-sm text-gray-500">Size Combos</p>
        </Card>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200 no-print">
        <div className="flex items-start gap-3">
          <div>
            <p className="text-sm text-blue-800">
              <strong>Margin Analysis:</strong> {heights.length} heights x {widths.length} widths = {data.length} size combinations.
              BOM costs match the BOM page calculation. MSRP at {dealerDiscount}% dealer discount.
              Hover cells for details.
            </p>
          </div>
        </div>
      </Card>

      {/* Margin Matrix Heatmap */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Margin % Matrix (Height x Width)</h3>
        <div className="overflow-x-auto">
          <table className="border-collapse text-xs">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-gray-100 px-2 py-2 border border-gray-300 text-right font-semibold" style={{ minWidth: 52 }}>
                  H \ W
                </th>
                {widths.map(w => (
                  <th key={w} className="px-2 py-2 border border-gray-300 bg-gray-100 text-center font-semibold whitespace-nowrap" style={{ minWidth: 44 }}>
                    {w / 12}'
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {heights.map(h => (
                <tr key={h}>
                  <td className="sticky left-0 z-10 bg-gray-100 px-2 py-2 border border-gray-300 font-semibold text-right whitespace-nowrap">
                    {h / 12}'
                  </td>
                  {widths.map(w => {
                    const cell = lookup.get(`${h}-${w}`);
                    if (!cell) return <td key={w} className="border border-gray-300 px-2 py-2 text-center">-</td>;
                    const mp = cell.marginPercent;
                    return (
                      <td
                        key={w}
                        className={clsx(
                          'px-2 py-2 border border-gray-300 text-center font-medium',
                          mp >= 50 ? 'bg-green-100 text-green-800' :
                          mp >= 30 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        )}
                        title={`${w/12}'W x ${h/12}'H\nBOM: $${cell.bomCost.toFixed(2)}\nMSRP: $${cell.msrp.toLocaleString()}\nDealer: $${cell.dealerPrice.toFixed(2)}\nMargin: $${cell.grossMargin.toFixed(2)} (${mp.toFixed(1)}%)`}
                      >
                        {mp.toFixed(0)}%
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex justify-center gap-6 text-xs">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-green-100 border border-green-300"></span> 50%+ (Healthy)
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-300"></span> 30-50% (Acceptable)
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-red-100 border border-red-300"></span> &lt;30% (Low)
          </span>
        </div>
      </Card>

      {/* Avg Margin by Height */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Average Margin by Height</h3>
        <div className="space-y-3">
          {marginByHeight.map((row) => (
            <div key={row.height} className="flex items-center gap-4">
              <div className="w-12 text-sm text-gray-600 text-right">{row.height / 12}'</div>
              <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden relative">
                <div
                  className={clsx(
                    'h-full rounded-full',
                    row.avgMargin >= 50 ? 'bg-green-500' :
                    row.avgMargin >= 30 ? 'bg-yellow-500' :
                    'bg-red-500'
                  )}
                  style={{ width: `${Math.min(100, Math.max(0, row.avgMargin))}%` }}
                />
                <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-700">
                  {row.avgMargin.toFixed(1)}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Detail Table */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Detailed Breakdown</h3>
          <select
            value={selectedHeight === 'all' ? 'all' : selectedHeight}
            onChange={(e) => setSelectedHeight(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
            className="no-print px-3 py-1 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Heights</option>
            {heights.map(h => (
              <option key={h} value={h}>{h / 12}' Height</option>
            ))}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Sq Ft</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">BOM Cost</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Items</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">MSRP</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Dealer Price</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Gross Margin</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Margin %</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {detailData.map((row: any, index: number) => (
                <tr key={index} className={clsx(
                  row.marginPercent < 30 ? 'bg-red-50' : row.marginPercent < 50 ? 'bg-yellow-50' : ''
                )}>
                  <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                    {row.width / 12}' x {row.height / 12}'
                  </td>
                  <td className="px-3 py-2 text-gray-600 text-right">{row.sqFt}</td>
                  <td className="px-3 py-2 text-gray-900 text-right">${row.bomCost.toFixed(2)}</td>
                  <td className="px-3 py-2 text-gray-600 text-right">{row.bomItemCount}</td>
                  <td className="px-3 py-2 text-gray-600 text-right">${row.msrp.toLocaleString()}</td>
                  <td className="px-3 py-2 text-gray-900 text-right">${row.dealerPrice.toFixed(2)}</td>
                  <td className={clsx(
                    'px-3 py-2 text-right font-medium',
                    row.grossMargin >= 0 ? 'text-green-600' : 'text-red-600'
                  )}>
                    ${row.grossMargin.toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className={clsx(
                      'px-2 py-1 rounded text-xs font-medium',
                      row.marginPercent >= 50 ? 'bg-green-100 text-green-800' :
                      row.marginPercent >= 30 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    )}>
                      {row.marginPercent.toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function DailyLaborBomReport({ data }: { data: any }) {
  if (!data || !data.summary) return null;
  const s = data.summary;
  const fmtMin = (m: number) => {
    const h = Math.floor(m / 60);
    const min = Math.round(m % 60);
    return h > 0 ? `${h}h ${min}m` : `${min}m`;
  };
  const fmtMoney = (n: number) => '$' + Number(n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return (
    <div className="space-y-6">
      <Card>
        <h3 className="text-lg font-semibold mb-3">Summary {data.from} → {data.to}</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div><div className="text-xs text-gray-500">Total Hours</div><div className="text-2xl font-bold">{s.totalHours}</div></div>
          <div><div className="text-xs text-gray-500">Total Cost</div><div className="text-2xl font-bold">{fmtMoney(s.totalCost)}</div></div>
          <div><div className="text-xs text-gray-500">Work Orders</div><div className="text-2xl font-bold">{s.workOrdersCompleted}</div></div>
          <div><div className="text-xs text-gray-500">Days w/ Activity</div><div className="text-2xl font-bold">{s.daysWithActivity}</div></div>
          <div><div className="text-xs text-gray-500">Avg $/Hour</div><div className="text-2xl font-bold">{s.totalHours > 0 ? fmtMoney(s.totalCost / s.totalHours) : '—'}</div></div>
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-semibold mb-3">By Day</h3>
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-right">Hours</th>
              <th className="px-3 py-2 text-right">Cost</th>
              <th className="px-3 py-2 text-right">WOs</th>
              <th className="px-3 py-2 text-right">Employees</th>
            </tr>
          </thead>
          <tbody>
            {(data.byDate || []).map((d: any) => (
              <tr key={d.date} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2 font-medium">{d.date}</td>
                <td className="px-3 py-2 text-right font-mono">{d.totalHours}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtMoney(d.totalCost)}</td>
                <td className="px-3 py-2 text-right font-mono">{d.workOrdersCompleted}</td>
                <td className="px-3 py-2 text-right font-mono">{d.employeeCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <h3 className="text-lg font-semibold mb-3">By Employee</h3>
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left">Employee</th><th className="px-3 py-2 text-right">Rate</th><th className="px-3 py-2 text-right">Hours</th><th className="px-3 py-2 text-right">Cost</th></tr></thead>
            <tbody>
              {(data.byEmployee || []).map((e: any) => (
                <tr key={e.employeeId} className="border-t hover:bg-gray-50">
                  <td className="px-3 py-2">{e.name}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{fmtMoney(e.hourlyRate)}/h</td>
                  <td className="px-3 py-2 text-right font-mono">{e.hours}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtMoney(e.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold mb-3">By Workstation</h3>
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left">Code</th><th className="px-3 py-2 text-right">Hours</th><th className="px-3 py-2 text-right">Cost</th><th className="px-3 py-2 text-right">WOs</th></tr></thead>
            <tbody>
              {(data.byStation || []).map((st: any) => (
                <tr key={st.code} className="border-t hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">{st.code}<div className="text-[11px] text-gray-500">{st.name}</div></td>
                  <td className="px-3 py-2 text-right font-mono">{st.hours}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtMoney(st.cost)}</td>
                  <td className="px-3 py-2 text-right font-mono">{st.workOrders}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}

function ForecastReport({ data }: { data: any }) {
  const s = data.summary || {};
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="text-center">
          <p className="text-2xl font-bold text-blue-600">${(s.totalPipelineValue || 0).toLocaleString()}</p>
          <p className="text-sm text-gray-500">Pipeline Value</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">{s.totalQuotes || 0}</p>
          <p className="text-sm text-gray-500">Active Quotes</p>
        </Card>
        <Card className="text-center">
          <p className={clsx(
            'text-2xl font-bold',
            (s.conversionRate || 0) >= 30 ? 'text-green-600' : (s.conversionRate || 0) >= 15 ? 'text-yellow-600' : 'text-red-600'
          )}>
            {(s.conversionRate || 0).toFixed(1)}%
          </p>
          <p className="text-sm text-gray-500">Conversion Rate</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-green-600">${(s.projectedRevenue || 0).toLocaleString()}</p>
          <p className="text-sm text-gray-500">Projected Revenue</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">{s.avgDaysToConvert || 0}d</p>
          <p className="text-sm text-gray-500">Avg Days to Convert</p>
        </Card>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <span className="text-blue-500 text-xl">ℹ️</span>
          <div className="text-sm text-blue-800">
            <strong>Revenue Forecast:</strong> Based on {s.lookbackDays || 90}-day lookback.
            {s.totalHistoricalOrders || 0} orders created, {s.totalConverted || 0} converted, {s.totalLost || 0} lost.
            Projected revenue applies the {(s.conversionRate || 0).toFixed(1)}% conversion rate to the current ${(s.totalPipelineValue || 0).toLocaleString()} pipeline.
          </div>
        </div>
      </Card>

      {/* Monthly Projection */}
      {(data.monthlyProjection || []).length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Pipeline Projection</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quotes</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quote Value</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Projected Conversions</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Projected Revenue</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(data.monthlyProjection || []).map((row: any, i: number) => (
                  <tr key={i}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.month}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">{row.quoteCount}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">${(row.quoteValue || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">{row.projectedConversions}</td>
                    <td className="px-4 py-3 text-sm text-green-600 font-medium text-right">${(row.projectedRevenue || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* By Dealer */}
      {(data.byDealer || []).length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pipeline by Dealer</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dealer</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quotes</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(data.byDealer || []).map((row: any, i: number) => (
                  <tr key={i}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.dealerName}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">{row.quoteCount}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">${(row.quoteValue || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Weekly Trends */}
      {(data.recentTrends || []).length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Trends</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Week Of</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Converted</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Lost</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(data.recentTrends || []).map((row: any, i: number) => (
                  <tr key={i}>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.week}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">{row.quotesCreated}</td>
                    <td className="px-4 py-3 text-sm text-green-600 text-right">{row.quotesConverted}</td>
                    <td className="px-4 py-3 text-sm text-red-600 text-right">{row.quotesLost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function LostQuotesReport({ data }: { data: any }) {
  const s = data.summary || {};
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="text-center">
          <p className="text-2xl font-bold text-red-600">{s.totalLost || 0}</p>
          <p className="text-sm text-gray-500">Total Lost</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-red-600">${(s.totalLostValue || 0).toLocaleString()}</p>
          <p className="text-sm text-gray-500">Lost Value</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">${(s.avgOrderValue || 0).toLocaleString()}</p>
          <p className="text-sm text-gray-500">Avg Order Value</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">{s.topReason || 'N/A'}</p>
          <p className="text-sm text-gray-500">Top Reason</p>
        </Card>
      </div>

      {(s.totalLost || 0) === 0 && (
        <Card className="bg-gray-50">
          <div className="text-center py-8 text-gray-500">
            No lost quotes found in this date range. When dealers mark quotes as lost, they will appear here.
          </div>
        </Card>
      )}

      {/* By Reason */}
      {(data.byReason || []).length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Loss Reasons</h3>
          <div className="space-y-3">
            {(data.byReason || []).map((row: any, i: number) => (
              <div key={i} className="flex items-center gap-4">
                <div className="w-48 text-sm text-gray-700 font-medium">{row.reason}</div>
                <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-red-400 rounded-full"
                    style={{ width: `${row.percentage}%` }}
                  />
                </div>
                <div className="w-16 text-sm text-gray-600 text-right">{row.count} ({row.percentage}%)</div>
                <div className="w-24 text-sm text-gray-900 text-right">${(row.totalValue || 0).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* By Dealer */}
      {(data.byDealer || []).length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Lost Quotes by Dealer</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dealer</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Lost Count</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Lost Value</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(data.byDealer || []).map((row: any, i: number) => (
                  <tr key={i}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.dealerName}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">{row.count}</td>
                    <td className="px-4 py-3 text-sm text-red-600 text-right">${(row.totalValue || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Recent Lost */}
      {(data.recentLost || []).length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Lost Quotes</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dealer</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lost Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(data.recentLost || []).map((row: any, i: number) => (
                  <tr key={i}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.orderNumber}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.dealerName}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">${(row.totalPrice || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.lostReason}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{row.lostAt ? new Date(row.lostAt).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function ExtrusionPOReportView({ data, params }: { data: any; params: { lookbackDays: number; leadTimeDays: number; targetCoverageDays: number; safetyStockPct: number } }) {
  const [hideZeroOrder, setHideZeroOrder] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const s = data.summary || {};
  const groups = data.byComponentType || [];
  const criticals = data.criticalItems || [];

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const blob = await api.reports.getExtrusionPOPdf(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `extrusion-po-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert('PDF generation failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setPdfLoading(false);
    }
  };

  const statusBadge = (status: string) => {
    const cls: Record<string, string> = {
      critical: 'bg-red-100 text-red-800',
      low: 'bg-yellow-100 text-yellow-800',
      adequate: 'bg-green-100 text-green-800',
      overstocked: 'bg-blue-100 text-blue-800',
    };
    return <span className={clsx('px-2 py-0.5 rounded text-xs font-semibold', cls[status] || 'bg-gray-100')}>{status.toUpperCase()}</span>;
  };

  const cleanType = (t: string) => t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="space-y-6">
      {/* Action Bar */}
      <div className="flex gap-3 no-print">
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          Print Report
        </button>
        <button
          onClick={handleDownloadPdf}
          disabled={pdfLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {pdfLoading ? 'Generating PDF...' : 'Download PDF'}
        </button>
        <label className="flex items-center gap-2 ml-auto text-sm text-gray-600">
          <input
            type="checkbox"
            checked={hideZeroOrder}
            onChange={(e) => setHideZeroOrder(e.target.checked)}
            className="rounded"
          />
          Hide items with 0 order qty
        </label>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="text-center">
          <p className="text-2xl font-bold text-green-600">${(s.totalPOCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          <p className="text-sm text-gray-500">Total PO Cost</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">{s.totalLineItems || 0}</p>
          <p className="text-sm text-gray-500">Line Items</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-red-600">{s.criticalItems || 0}</p>
          <p className="text-sm text-gray-500">Critical Items</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-yellow-600">{s.lowStockItems || 0}</p>
          <p className="text-sm text-gray-500">Low Stock</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">{s.totalAnalyzed || 0}</p>
          <p className="text-sm text-gray-500">Total Analyzed</p>
        </Card>
      </div>

      {/* Parameters Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <p className="text-sm text-blue-800">
          <strong>Parameters:</strong> Based on {s.lookbackDays || 90}-day consumption history, {s.leadTimeDays || 45}-day lead time,
          {' '}{s.targetCoverageDays || 90}-day target coverage, {s.safetyStockPct || 15}% safety stock buffer.
        </p>
      </Card>

      {/* Critical Items Alert */}
      {criticals.length > 0 && (
        <Card className="border-red-200">
          <h3 className="text-lg font-semibold text-red-800 mb-3">Critical Items — Order Immediately</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-red-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Available</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rate/Day</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Runout</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Order Qty</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Line Cost</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {criticals.map((item: any, i: number) => (
                  <tr key={i} className="bg-red-50">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-3">
                        {item.imageUrl && <img src={item.imageUrl} alt="" className="w-10 h-10 object-contain rounded border bg-white flex-shrink-0" />}
                        <div>
                          <div className="font-semibold text-gray-900">{item.partNumber}</div>
                          <div className="text-xs text-gray-500">{item.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">{item.availableStock}</td>
                    <td className="px-3 py-2 text-right">{item.dailyRate?.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-bold text-red-700">{item.projectedRunoutDays !== null ? item.projectedRunoutDays + 'd' : '-'}</td>
                    <td className="px-3 py-2 text-right font-bold">{item.orderQty}</td>
                    <td className="px-3 py-2 text-right font-semibold">${(item.lineCost || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Component Type Groups */}
      {groups.map((group: any) => {
        const items = hideZeroOrder ? (group.items || []).filter((i: any) => i.orderQty > 0) : (group.items || []);
        if (items.length === 0) return null;
        return (
          <Card key={group.componentType}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">
                {cleanType(group.componentType)}
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({items.length} items, ${(group.subtotalCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                </span>
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Stock</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Alloc</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avail</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rate/Day</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Pipeline</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Runout</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Order Qty</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Unit Cost</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Line Total</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item: any, idx: number) => (
                    <tr key={idx} className={clsx(
                      item.status === 'critical' ? 'bg-red-50' :
                      item.status === 'low' ? 'bg-yellow-50' :
                      item.status === 'overstocked' ? 'bg-blue-50' : ''
                    )}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-3">
                          {item.imageUrl && <img src={item.imageUrl} alt="" className="w-10 h-10 object-contain rounded border bg-white flex-shrink-0" />}
                          <div>
                            <div className="font-semibold text-gray-900">{item.partNumber}</div>
                            <div className="text-xs text-gray-500 max-w-[260px] truncate" title={item.description}>{item.description}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">{item.currentStock}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{item.allocated}</td>
                      <td className="px-3 py-2 text-right text-gray-900">{item.availableStock}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{item.dailyRate?.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{item.pipelineDemand}</td>
                      <td className="px-3 py-2 text-right font-medium">{item.projectedRunoutDays !== null ? item.projectedRunoutDays + 'd' : '-'}</td>
                      <td className="px-3 py-2 text-right font-bold text-gray-900">{item.orderQty}</td>
                      <td className="px-3 py-2 text-right text-gray-600">${(item.unitCost || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900">${(item.lineCost || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-center">{statusBadge(item.status)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-100">
                  <tr>
                    <td colSpan={7} className="px-3 py-2 text-right font-semibold text-gray-700">Group Subtotal</td>
                    <td className="px-3 py-2 text-right font-bold text-gray-900">{group.subtotalQty}</td>
                    <td className="px-3 py-2"></td>
                    <td className="px-3 py-2 text-right font-bold text-gray-900">${(group.subtotalCost || 0).toFixed(2)}</td>
                    <td className="px-3 py-2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        );
      })}

      {/* Grand Total */}
      <Card className="bg-green-50 border-green-200">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-green-800">Grand Total</h3>
          <p className="text-2xl font-bold text-green-700">
            ${(s.totalPOCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
      </Card>
    </div>
  );
}

function ReportContent({ type, data }: { type: ReportType; data: any }) {
  switch (type) {
    case 'production':
      return <ProductionReport data={data} />;
    case 'sales':
      return <SalesReport data={data} />;
    case 'inventory':
      return <InventoryReport data={data} />;
    case 'dealer':
      return <DealerReport data={data} />;
    default:
      return null;
  }
}

function ProductionReport({ data }: { data: any }) {
  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">{data.totalOrders}</p>
          <p className="text-sm text-gray-500">Orders Completed</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">{data.totalUnits}</p>
          <p className="text-sm text-gray-500">Units Produced</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">{data.avgLeadTime} days</p>
          <p className="text-sm text-gray-500">Avg Lead Time</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-green-600">{data.onTimeRate}%</p>
          <p className="text-sm text-gray-500">On-Time Rate</p>
        </Card>
      </div>

      {/* Production by Workstation */}
      <Card title="Production by Workstation">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Workstation</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Completed</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">In Progress</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Blocked</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Avg Time</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.byWorkstation.map((ws: any) => (
                <tr key={ws.name}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{ws.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{ws.completed}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{ws.inProgress}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{ws.blocked}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 text-right">{ws.avgTime}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function SalesReport({ data }: { data: any }) {
  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">{data.totalOrders}</p>
          <p className="text-sm text-gray-500">Total Orders</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-green-600">${data.totalRevenue.toLocaleString()}</p>
          <p className="text-sm text-gray-500">Total Revenue</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">${data.avgOrderValue.toLocaleString()}</p>
          <p className="text-sm text-gray-500">Avg Order Value</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">{data.totalUnits}</p>
          <p className="text-sm text-gray-500">Units Sold</p>
        </Card>
      </div>

      {/* Sales by Product Type */}
      <Card title="Sales by Product Type">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Type</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Units</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% of Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.byProductType.map((pt: any) => (
                <tr key={pt.type}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 capitalize">{pt.type}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{pt.units}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">${pt.revenue.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 text-right">{pt.percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function InventoryReport({ data }: { data: any }) {
  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">{data.totalItems}</p>
          <p className="text-sm text-gray-500">Total Items</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-green-600">${data.totalValue.toLocaleString()}</p>
          <p className="text-sm text-gray-500">Total Value</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-orange-600">{data.lowStockItems}</p>
          <p className="text-sm text-gray-500">Low Stock</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-red-600">{data.outOfStockItems}</p>
          <p className="text-sm text-gray-500">Out of Stock</p>
        </Card>
      </div>

      {/* Low Stock Items */}
      <Card title="Items Requiring Attention">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">On Hand</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Min Level</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.lowStockList.map((item: any) => (
                <tr key={item.partNo} className={item.onHand === 0 ? 'bg-red-50' : 'bg-orange-50'}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.partNo}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{item.description}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.onHand}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 text-right">{item.minLevel}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      item.onHand === 0
                        ? 'bg-red-100 text-red-800'
                        : 'bg-orange-100 text-orange-800'
                    }`}>
                      {item.onHand === 0 ? 'Out of Stock' : 'Low Stock'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function DealerReport({ data }: { data: any }) {
  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">{data.totalDealers}</p>
          <p className="text-sm text-gray-500">Active Dealers</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">{data.newDealers}</p>
          <p className="text-sm text-gray-500">New This Period</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-green-600">${data.totalRevenue.toLocaleString()}</p>
          <p className="text-sm text-gray-500">Total Revenue</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">${data.avgDealerRevenue.toLocaleString()}</p>
          <p className="text-sm text-gray-500">Avg Per Dealer</p>
        </Card>
      </div>

      {/* Top Dealers */}
      <Card title="Top Dealers by Revenue">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dealer</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Orders</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Avg Order</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.topDealers.map((dealer: any, index: number) => (
                <tr key={dealer.id}>
                  <td className="px-4 py-3 text-sm">
                    <span className="font-medium text-gray-900">{index + 1}. {dealer.name}</span>
                    <span className="text-gray-500 ml-2">({dealer.location})</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{dealer.orders}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">${dealer.revenue.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 text-right">${dealer.avgOrder.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function ShowroomImpactReport({ data }: { data: any }) {
  const fmt = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const s = data.summary?.withShowroom || { dealerCount: 0, orderCount: 0, totalRevenue: 0, avgOrderValue: 0, ordersLast90d: 0, revenueLast90d: 0 };
  const ns = data.summary?.withoutShowroom || { dealerCount: 0, orderCount: 0, totalRevenue: 0, avgOrderValue: 0, ordersLast90d: 0, revenueLast90d: 0 };
  const comp = data.comparison || {};

  return (
    <div className="space-y-6">
      <Card>
        <h3 className="text-lg font-semibold mb-4">Showroom vs No Showroom</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead><tr className="bg-gray-50">
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-purple-600 uppercase">With Showroom</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Without Showroom</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Lift</th>
            </tr></thead>
            <tbody className="divide-y">
              <tr><td className="px-4 py-2">Dealers</td><td className="px-4 py-2 text-right font-bold text-purple-700">{s.dealerCount}</td><td className="px-4 py-2 text-right">{ns.dealerCount}</td><td className="px-4 py-2 text-right text-gray-400">-</td></tr>
              <tr><td className="px-4 py-2">Total Orders</td><td className="px-4 py-2 text-right font-bold text-purple-700">{s.orderCount}</td><td className="px-4 py-2 text-right">{ns.orderCount}</td><td className="px-4 py-2 text-right text-gray-400">-</td></tr>
              <tr><td className="px-4 py-2">Total Revenue</td><td className="px-4 py-2 text-right font-bold text-purple-700">{fmt(s.totalRevenue)}</td><td className="px-4 py-2 text-right">{fmt(ns.totalRevenue)}</td><td className="px-4 py-2 text-right text-gray-400">-</td></tr>
              <tr className="bg-blue-50"><td className="px-4 py-2 font-medium">Avg Revenue / Dealer</td><td className="px-4 py-2 text-right font-bold text-purple-700">{fmt(comp.avgRevenuePerDealer?.showroom || 0)}</td><td className="px-4 py-2 text-right">{fmt(comp.avgRevenuePerDealer?.noShowroom || 0)}</td><td className="px-4 py-2 text-right font-bold" style={{color:(comp.avgRevenuePerDealer?.lift||0)>0?'#16a34a':'#dc2626'}}>{(comp.avgRevenuePerDealer?.lift||0)>0?'+':''}{(comp.avgRevenuePerDealer?.lift||0).toFixed(1)}%</td></tr>
              <tr className="bg-blue-50"><td className="px-4 py-2 font-medium">Avg Orders / Dealer</td><td className="px-4 py-2 text-right font-bold text-purple-700">{(comp.avgOrdersPerDealer?.showroom||0).toFixed(1)}</td><td className="px-4 py-2 text-right">{(comp.avgOrdersPerDealer?.noShowroom||0).toFixed(1)}</td><td className="px-4 py-2 text-right font-bold" style={{color:(comp.avgOrdersPerDealer?.lift||0)>0?'#16a34a':'#dc2626'}}>{(comp.avgOrdersPerDealer?.lift||0)>0?'+':''}{(comp.avgOrdersPerDealer?.lift||0).toFixed(1)}%</td></tr>
              <tr><td className="px-4 py-2">Avg Order Value</td><td className="px-4 py-2 text-right font-bold text-purple-700">{fmt(s.avgOrderValue)}</td><td className="px-4 py-2 text-right">{fmt(ns.avgOrderValue)}</td><td className="px-4 py-2 text-right text-gray-400">-</td></tr>
              <tr><td className="px-4 py-2">Orders (Last 90d)</td><td className="px-4 py-2 text-right font-bold text-purple-700">{s.ordersLast90d}</td><td className="px-4 py-2 text-right">{ns.ordersLast90d}</td><td className="px-4 py-2 text-right text-gray-400">-</td></tr>
              <tr><td className="px-4 py-2">Revenue (Last 90d)</td><td className="px-4 py-2 text-right font-bold text-purple-700">{fmt(s.revenueLast90d)}</td><td className="px-4 py-2 text-right">{fmt(ns.revenueLast90d)}</td><td className="px-4 py-2 text-right text-gray-400">-</td></tr>
            </tbody>
          </table>
        </div>
      </Card>
      {data.showroomDealers?.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold mb-3">Dealers With Showroom</h3>
          <table className="min-w-full text-sm divide-y divide-gray-200">
            <thead className="bg-gray-50"><tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Dealer</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Orders</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Order</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">First Order</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Order</th>
            </tr></thead>
            <tbody className="divide-y">{data.showroomDealers.map((d:any)=>(
              <tr key={d.id}><td className="px-4 py-2 font-medium">{d.companyName}</td><td className="px-4 py-2 text-right">{d.orderCount}</td><td className="px-4 py-2 text-right text-green-700 font-medium">{fmt(d.totalRevenue)}</td><td className="px-4 py-2 text-right">{fmt(d.avgOrderValue)}</td><td className="px-4 py-2 text-gray-500 text-xs">{d.firstOrder?new Date(d.firstOrder).toLocaleDateString():'-'}</td><td className="px-4 py-2 text-gray-500 text-xs">{d.lastOrder?new Date(d.lastOrder).toLocaleDateString():'-'}</td></tr>
            ))}</tbody>
          </table>
        </Card>
      )}
      {data.nonShowroomDealers?.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold mb-1">Top Dealers Without Showroom (Comparison)</h3>
          <p className="text-sm text-gray-500 mb-3">Top 20 by revenue</p>
          <table className="min-w-full text-sm divide-y divide-gray-200">
            <thead className="bg-gray-50"><tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Dealer</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Orders</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Order</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">First Order</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Order</th>
            </tr></thead>
            <tbody className="divide-y">{data.nonShowroomDealers.map((d:any)=>(
              <tr key={d.id}><td className="px-4 py-2 font-medium">{d.companyName}</td><td className="px-4 py-2 text-right">{d.orderCount}</td><td className="px-4 py-2 text-right text-green-700 font-medium">{fmt(d.totalRevenue)}</td><td className="px-4 py-2 text-right">{fmt(d.avgOrderValue)}</td><td className="px-4 py-2 text-gray-500 text-xs">{d.firstOrder?new Date(d.firstOrder).toLocaleDateString():'-'}</td><td className="px-4 py-2 text-gray-500 text-xs">{d.lastOrder?new Date(d.lastOrder).toLocaleDateString():'-'}</td></tr>
            ))}</tbody>
          </table>
        </Card>
      )}
      {s.dealerCount === 0 && (
        <Card><div className="text-center py-8 text-gray-500"><p className="text-lg font-medium mb-2">No showroom dealers yet</p><p className="text-sm">Mark dealers as having a showroom on their Dealer Detail page to start tracking impact.</p></div></Card>
      )}
    </div>
  );
}
